import { useMemo, useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { Ticket, CheckCircle, Clock, Hash, Plus, Copy, Check, FileDown, Printer, Ban, Gift, BadgeDollarSign, Search } from "lucide-react";
import { format } from "date-fns";

type VoucherState = "unused" | "redeemed" | "revoked" | "expired";

interface VoucherRow {
  id: string;
  code: string;
  displayCode: string;
  kind: "paid" | "courtesy" | string;
  durationDays: number;
  status: VoucherState;
  batchId: string | null;
  serial: number | null;
  label: string | null;
  expiresAt: string | null;
  createdAt: string;
  redeemedAt: string | null;
  redeemedBy: string | null;
  source: string;
  partnerName: string | null;
}

interface Batch {
  id: string;
  label: string;
  kind: string;
  durationDays: number;
  quantity: number;
  expiresAt: string | null;
  note: string | null;
  source: string;
  createdAt: string;
  redeemed: number;
  unused: number;
  revoked: number;
}

interface VoucherData {
  total: number;
  stats: { total: number; unused: number; redeemed: number; revoked: number; expired: number; daysRedeemed: number; byKind: Record<string, { total: number; redeemed: number; unused: number }> };
  batches: Batch[];
  vouchers: VoucherRow[];
}

const DURATION_PRESETS = [
  { value: "7", label: "1 week (7 days)" },
  { value: "14", label: "2 weeks (14 days)" },
  { value: "30", label: "1 month (30 days)" },
  { value: "90", label: "3 months (90 days)" },
  { value: "180", label: "6 months (180 days)" },
  { value: "365", label: "1 year (365 days)" },
  { value: "custom", label: "Custom number of days" },
];

function daysLabel(n: number) {
  if (n === 7) return "1 week";
  if (n === 14) return "2 weeks";
  if (n === 30) return "1 month";
  if (n === 90) return "3 months";
  if (n === 180) return "6 months";
  if (n === 365) return "1 year";
  return `${n} days`;
}

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(text).catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <Button size="icon" variant="ghost" className="h-6 w-6 shrink-0" onClick={copy} aria-label="Copy code">
      {copied ? <Check className="w-3 h-3 text-green-600" /> : <Copy className="w-3 h-3" />}
    </Button>
  );
}

function KindBadge({ kind }: { kind: string }) {
  return kind === "courtesy"
    ? <Badge variant="outline" className="gap-1 border-violet-300 text-violet-700"><Gift className="w-3 h-3" /> Courtesy</Badge>
    : <Badge variant="outline" className="gap-1 border-emerald-300 text-emerald-700"><BadgeDollarSign className="w-3 h-3" /> Paid</Badge>;
}

function StatusBadge({ status }: { status: VoucherState }) {
  const variant = status === "redeemed" ? "default" : status === "unused" ? "secondary" : "outline";
  const cls = status === "revoked" ? "text-red-600 border-red-300" : status === "expired" ? "text-amber-700 border-amber-300" : "";
  return <Badge variant={variant} className={cls}>{status}</Badge>;
}

export default function AdminVouchers() {
  const { toast } = useToast();
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [form, setForm] = useState({ label: "", kind: "paid", preset: "30", customDays: "", quantity: "10", expiresAt: "", note: "" });
  const [generated, setGenerated] = useState<{ batchId: string; codes: string[] } | null>(null);

  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [kindFilter, setKindFilter] = useState<string>("all");
  const [batchFilter, setBatchFilter] = useState<string>("all");
  const [search, setSearch] = useState("");

  const queryKey = useMemo(() => {
    const p = new URLSearchParams();
    if (statusFilter !== "all") p.set("status", statusFilter);
    if (kindFilter !== "all") p.set("kind", kindFilter);
    if (batchFilter !== "all") p.set("batchId", batchFilter);
    if (search.trim()) p.set("q", search.trim());
    p.set("limit", "500");
    return [`/api/admin/vouchers?${p.toString()}`];
  }, [statusFilter, kindFilter, batchFilter, search]);

  const { data, isLoading } = useQuery<VoucherData>({ queryKey });

  const durationDays = form.preset === "custom" ? parseInt(form.customDays, 10) || 0 : parseInt(form.preset, 10);

  const generateMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/admin/vouchers/generate", {
        label: form.label.trim() || undefined,
        kind: form.kind,
        durationDays,
        quantity: parseInt(form.quantity, 10) || 1,
        expiresAt: form.expiresAt || undefined,
        note: form.note.trim() || undefined,
      });
      return res.json();
    },
    onSuccess: (result) => {
      setGenerated({ batchId: result.batch.id, codes: result.vouchers.map((v: any) => v.displayCode) });
      queryClient.invalidateQueries({ predicate: (q) => String(q.queryKey[0]).startsWith("/api/admin/vouchers") });
      toast({ title: "Vouchers created", description: `${result.vouchers.length} ${form.kind} voucher(s), ${daysLabel(durationDays)} each` });
    },
    onError: (err: any) => toast({ title: "Could not create vouchers", description: err.message || "Try again", variant: "destructive" }),
  });

  const revokeMutation = useMutation({
    mutationFn: async (code: string) => (await apiRequest("POST", `/api/admin/vouchers/${encodeURIComponent(code)}/revoke`, {})).json(),
    onSuccess: (_r, code) => {
      queryClient.invalidateQueries({ predicate: (q) => String(q.queryKey[0]).startsWith("/api/admin/vouchers") });
      toast({ title: "Voucher revoked", description: code });
    },
    onError: (err: any) => toast({ title: "Could not revoke", description: err.message || "Try again", variant: "destructive" }),
  });

  const revokeBatchMutation = useMutation({
    mutationFn: async (batchId: string) => (await apiRequest("POST", `/api/admin/vouchers/batches/${batchId}/revoke`, {})).json(),
    onSuccess: (r: any) => {
      queryClient.invalidateQueries({ predicate: (q) => String(q.queryKey[0]).startsWith("/api/admin/vouchers") });
      toast({ title: `${r.revoked} voucher${r.revoked === 1 ? "" : "s"} revoked`, description: r.keptRedeemed ? `${r.keptRedeemed} already redeemed keep their access.` : "The batch can no longer be redeemed." });
    },
    onError: (err: any) => toast({ title: "Could not revoke the batch", description: err.message || "Try again", variant: "destructive" }),
  });

  const closeCreate = () => {
    setIsCreateOpen(false);
    setGenerated(null);
    setForm({ label: "", kind: "paid", preset: "30", customDays: "", quantity: "10", expiresAt: "", note: "" });
  };

  const stats = data?.stats ?? { total: 0, unused: 0, redeemed: 0, revoked: 0, expired: 0, daysRedeemed: 0, byKind: {} };
  const batches = data?.batches ?? [];
  const vouchers = data?.vouchers ?? [];
  const paid = stats.byKind?.paid ?? { total: 0, redeemed: 0, unused: 0 };
  const courtesy = stats.byKind?.courtesy ?? { total: 0, redeemed: 0, unused: 0 };
  const canGenerate = durationDays >= 1 && durationDays <= 3650 && (parseInt(form.quantity, 10) || 0) >= 1;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold">Vouchers</h1>
          <p className="text-muted-foreground">Prepaid and courtesy access codes. Print a batch, hand out the cards, and every code unlocks the account for its number of days. Each card carries the logo, a redeem QR and App Store and Google Play QR codes.</p>
        </div>
        <Dialog open={isCreateOpen} onOpenChange={(open) => { if (!open) closeCreate(); else setIsCreateOpen(true); }}>
          <DialogTrigger asChild>
            <Button className="gap-2" data-testid="voucher-create"><Plus className="w-4 h-4" /> Create vouchers</Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>{generated ? "Vouchers ready" : "Create vouchers"}</DialogTitle>
              <DialogDescription>{generated ? "Print the sheet or copy the codes. They stay in the list below." : "One batch, one duration, one kind. Up to 1,000 codes at a time."}</DialogDescription>
            </DialogHeader>

            {generated ? (
              <div className="space-y-3">
                <div className="flex gap-2">
                  <Button asChild className="flex-1 gap-2">
                    <a href={`/api/admin/vouchers/batches/${generated.batchId}/pdf`} target="_blank" rel="noreferrer"><Printer className="w-4 h-4" /> Print sheet (PDF)</a>
                  </Button>
                  <Button asChild variant="outline" className="flex-1 gap-2">
                    <a href={`/api/admin/vouchers/batches/${generated.batchId}/csv`}><FileDown className="w-4 h-4" /> Download CSV</a>
                  </Button>
                </div>
                <div className="space-y-1.5 max-h-60 overflow-y-auto rounded-md border p-2">
                  {generated.codes.map((code) => (
                    <div key={code} className="flex items-center gap-2 px-2 py-1 font-mono text-sm">
                      <span className="flex-1">{code}</span>
                      <CopyButton text={code} />
                    </div>
                  ))}
                </div>
                {generated.codes.length > 1 && (
                  <Button variant="ghost" className="w-full gap-2" onClick={() => { navigator.clipboard.writeText(generated.codes.join("\n")).catch(() => {}); toast({ title: "Copied all codes" }); }}>
                    <Copy className="w-4 h-4" /> Copy all codes
                  </Button>
                )}
                <DialogFooter><Button onClick={closeCreate}>Done</Button></DialogFooter>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Batch label</Label>
                  <Input placeholder="e.g. St. Andrew Prep, Term 1 or Rotary giveaway" value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} data-testid="voucher-label" />
                  <p className="text-xs text-muted-foreground">Printed in the sheet footer and shown here. Left blank, it is built from the settings below.</p>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label>Kind</Label>
                    <Select value={form.kind} onValueChange={(v) => setForm({ ...form, kind: v })}>
                      <SelectTrigger data-testid="voucher-kind"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="paid">Paid (sold to a school, partner or parent)</SelectItem>
                        <SelectItem value="courtesy">Courtesy (given away, not for resale)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Quantity</Label>
                    <Input type="number" min={1} max={1000} value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} data-testid="voucher-quantity" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label>Access per voucher</Label>
                    <Select value={form.preset} onValueChange={(v) => setForm({ ...form, preset: v })}>
                      <SelectTrigger data-testid="voucher-duration"><SelectValue /></SelectTrigger>
                      <SelectContent>{DURATION_PRESETS.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>{form.preset === "custom" ? "Days" : "Use by (optional)"}</Label>
                    {form.preset === "custom"
                      ? <Input type="number" min={1} max={3650} placeholder="Days of access" value={form.customDays} onChange={(e) => setForm({ ...form, customDays: e.target.value })} />
                      : <Input type="date" value={form.expiresAt} onChange={(e) => setForm({ ...form, expiresAt: e.target.value })} />}
                  </div>
                </div>
                {form.preset === "custom" && (
                  <div className="space-y-2">
                    <Label>Use by (optional)</Label>
                    <Input type="date" value={form.expiresAt} onChange={(e) => setForm({ ...form, expiresAt: e.target.value })} />
                  </div>
                )}
                <div className="space-y-2">
                  <Label>Internal note (optional)</Label>
                  <Textarea rows={2} placeholder="Who paid, invoice number, who it was given to" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} />
                </div>
                <p className="text-sm text-muted-foreground">
                  {parseInt(form.quantity, 10) || 0} {form.kind} voucher(s), {durationDays ? daysLabel(durationDays) : "?"} of full access each{form.expiresAt ? `, to be used by ${form.expiresAt}` : ""}.
                </p>
                <DialogFooter>
                  <Button variant="outline" onClick={closeCreate}>Cancel</Button>
                  <Button onClick={() => generateMutation.mutate()} disabled={!canGenerate || generateMutation.isPending} data-testid="voucher-generate">
                    {generateMutation.isPending ? "Creating..." : "Create"}
                  </Button>
                </DialogFooter>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card><CardHeader className="pb-2"><CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2"><Hash className="w-4 h-4" /> Total</CardTitle></CardHeader><CardContent><div className="text-3xl font-bold">{stats.total}</div></CardContent></Card>
        <Card><CardHeader className="pb-2"><CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2"><Clock className="w-4 h-4" /> Unused</CardTitle></CardHeader><CardContent><div className="text-3xl font-bold text-amber-600">{stats.unused}</div></CardContent></Card>
        <Card><CardHeader className="pb-2"><CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2"><CheckCircle className="w-4 h-4" /> Redeemed</CardTitle></CardHeader><CardContent><div className="text-3xl font-bold text-green-600">{stats.redeemed}</div><p className="text-xs text-muted-foreground">{stats.daysRedeemed.toLocaleString()} days of access</p></CardContent></Card>
        <Card><CardHeader className="pb-2"><CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2"><BadgeDollarSign className="w-4 h-4" /> Paid</CardTitle></CardHeader><CardContent><div className="text-3xl font-bold">{paid.total}</div><p className="text-xs text-muted-foreground">{paid.redeemed} redeemed, {paid.unused} unused</p></CardContent></Card>
        <Card><CardHeader className="pb-2"><CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2"><Gift className="w-4 h-4" /> Courtesy</CardTitle></CardHeader><CardContent><div className="text-3xl font-bold">{courtesy.total}</div><p className="text-xs text-muted-foreground">{courtesy.redeemed} redeemed, {courtesy.unused} unused</p></CardContent></Card>
      </div>

      <Card>
        <CardHeader><CardTitle className="flex items-center gap-2"><Printer className="w-5 h-5" /> Batches</CardTitle></CardHeader>
        <CardContent>
          {batches.length === 0 ? (
            <p className="text-muted-foreground text-center py-6">No batches yet. Create vouchers to get a printable sheet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-muted-foreground">
                    <th className="py-2 pr-4 font-medium">Label</th>
                    <th className="py-2 pr-4 font-medium">Kind</th>
                    <th className="py-2 pr-4 font-medium">Access</th>
                    <th className="py-2 pr-4 font-medium text-right">Codes</th>
                    <th className="py-2 pr-4 font-medium text-right">Redeemed</th>
                    <th className="py-2 pr-4 font-medium">Use by</th>
                    <th className="py-2 pr-4 font-medium">Created</th>
                    <th className="py-2 font-medium"></th>
                  </tr>
                </thead>
                <tbody>
                  {batches.map((b) => (
                    <tr key={b.id} className="border-b last:border-0">
                      <td className="py-2.5 pr-4">
                        <button className="font-medium text-left hover:underline" onClick={() => setBatchFilter(b.id)} title="Show this batch's codes">{b.label}</button>
                        {b.note && <div className="text-xs text-muted-foreground">{b.note}</div>}
                        {b.source !== "admin" && <Badge variant="secondary" className="ml-2">{b.source}</Badge>}
                      </td>
                      <td className="py-2.5 pr-4"><KindBadge kind={b.kind} /></td>
                      <td className="py-2.5 pr-4">{daysLabel(b.durationDays)}</td>
                      <td className="py-2.5 pr-4 text-right tabular-nums">{b.quantity}</td>
                      <td className="py-2.5 pr-4 text-right tabular-nums">{b.redeemed}{b.revoked ? <span className="text-muted-foreground"> ({b.revoked} revoked)</span> : null}</td>
                      <td className="py-2.5 pr-4 text-muted-foreground">{b.expiresAt ? format(new Date(b.expiresAt), "MMM d, yyyy") : "No limit"}</td>
                      <td className="py-2.5 pr-4 text-muted-foreground">{format(new Date(b.createdAt), "MMM d, yyyy")}</td>
                      <td className="py-2.5 whitespace-nowrap">
                        <Button asChild size="sm" variant="outline" className="gap-1 mr-2"><a href={`/api/admin/vouchers/batches/${b.id}/pdf`} target="_blank" rel="noreferrer"><Printer className="w-3.5 h-3.5" /> PDF</a></Button>
                        <Button asChild size="sm" variant="ghost" className="gap-1"><a href={`/api/admin/vouchers/batches/${b.id}/csv`}><FileDown className="w-3.5 h-3.5" /> CSV</a></Button>
                        {b.unused > 0 && (
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button size="sm" variant="ghost" className="gap-1 text-red-600 hover:text-red-700" disabled={revokeBatchMutation.isPending}><Ban className="w-3.5 h-3.5" /> Revoke unused</Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Revoke {b.unused} unused code{b.unused === 1 ? "" : "s"} in "{b.label}"?</AlertDialogTitle>
                                <AlertDialogDescription>
                                  Nobody will be able to redeem them after this, including cards already handed out.{b.redeemed > 0 ? ` The ${b.redeemed} already redeemed keep their access.` : ""} This cannot be undone.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Keep them</AlertDialogCancel>
                                <AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={() => revokeBatchMutation.mutate(b.id)}>Revoke {b.unused}</AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <CardTitle className="flex items-center gap-2"><Ticket className="w-5 h-5" /> Codes {data ? <span className="text-sm font-normal text-muted-foreground">({data.total})</span> : null}</CardTitle>
            <div className="flex items-center gap-2 flex-wrap">
              <div className="relative">
                <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-muted-foreground" />
                <Input className="pl-8 w-48" placeholder="Code or label" value={search} onChange={(e) => setSearch(e.target.value)} />
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All statuses</SelectItem>
                  <SelectItem value="unused">Unused</SelectItem>
                  <SelectItem value="redeemed">Redeemed</SelectItem>
                  <SelectItem value="expired">Expired</SelectItem>
                  <SelectItem value="revoked">Revoked</SelectItem>
                </SelectContent>
              </Select>
              <Select value={kindFilter} onValueChange={setKindFilter}>
                <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All kinds</SelectItem>
                  <SelectItem value="paid">Paid</SelectItem>
                  <SelectItem value="courtesy">Courtesy</SelectItem>
                </SelectContent>
              </Select>
              {batchFilter !== "all" && <Button size="sm" variant="ghost" onClick={() => setBatchFilter("all")}>Clear batch</Button>}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <p className="text-muted-foreground text-center py-8">Loading...</p>
          ) : vouchers.length === 0 ? (
            <p className="text-muted-foreground text-center py-8">No vouchers match.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-muted-foreground">
                    <th className="py-2 pr-4 font-medium">Code</th>
                    <th className="py-2 pr-4 font-medium">Kind</th>
                    <th className="py-2 pr-4 font-medium">Access</th>
                    <th className="py-2 pr-4 font-medium">Batch</th>
                    <th className="py-2 pr-4 font-medium">Status</th>
                    <th className="py-2 pr-4 font-medium">Use by</th>
                    <th className="py-2 pr-4 font-medium">Redeemed</th>
                    <th className="py-2 font-medium"></th>
                  </tr>
                </thead>
                <tbody>
                  {vouchers.map((v) => (
                    <tr key={v.id} className="border-b last:border-0">
                      <td className="py-2.5 pr-4 font-mono font-semibold whitespace-nowrap"><span className="inline-flex items-center gap-1">{v.displayCode}<CopyButton text={v.displayCode} /></span></td>
                      <td className="py-2.5 pr-4"><KindBadge kind={v.kind} /></td>
                      <td className="py-2.5 pr-4 whitespace-nowrap">{daysLabel(v.durationDays)}</td>
                      <td className="py-2.5 pr-4 text-muted-foreground">{v.label ?? (v.partnerName ? `Partner: ${v.partnerName}` : "Single")}{v.serial ? <span className="ml-1 text-xs">#{v.serial}</span> : null}</td>
                      <td className="py-2.5 pr-4"><StatusBadge status={v.status} /></td>
                      <td className="py-2.5 pr-4 text-muted-foreground whitespace-nowrap">{v.expiresAt ? format(new Date(v.expiresAt), "MMM d, yyyy") : "No limit"}</td>
                      <td className="py-2.5 pr-4 text-muted-foreground">{v.redeemedAt ? <>{format(new Date(v.redeemedAt), "MMM d, yyyy")}{v.redeemedBy ? <div className="text-xs">{v.redeemedBy}</div> : null}</> : "–"}</td>
                      <td className="py-2.5">
                        {v.status === "unused" && (
                          <Button size="sm" variant="ghost" className="gap-1 text-red-600" onClick={() => { if (confirm(`Revoke ${v.displayCode}? It can no longer be redeemed.`)) revokeMutation.mutate(v.code); }}>
                            <Ban className="w-3.5 h-3.5" /> Revoke
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base">Partner API</CardTitle></CardHeader>
        <CardContent className="text-sm text-muted-foreground space-y-1">
          <p>Partners with an API key (Admin, API keys) can run the same thing from their own systems, with the <code className="font-mono">x-api-key</code> header:</p>
          <ul className="list-disc ml-5 space-y-0.5 font-mono text-xs">
            <li>POST /api/partner/vouchers/generate  {"{ durationDays, kind, quantity, label, expiresAt }"}</li>
            <li>GET /api/partner/vouchers?status=&amp;kind=&amp;batchId=&amp;q=</li>
            <li>GET /api/partner/vouchers/batches  and  /batches/:id/pdf  /batches/:id/csv</li>
            <li>GET /api/partner/vouchers/:code  and  POST /api/partner/vouchers/:code/revoke</li>
            <li>POST /api/partner/vouchers/batches/:id/revoke  (every unused code in the batch; redeemed ones keep their access)</li>
          </ul>
          <p>Codes are redeemed at signup on the web, iOS and Android, on the Subscribe page, or in Settings, Subscription.</p>
        </CardContent>
      </Card>
    </div>
  );
}
